// WalkthroughController.js - Controls when to start the app walkthrough
//
// AIT-280: react-native-copilot keeps its dimmed, touch-blocking Modal up
// until stop() is called. If the tour starts with no mounted steps, or the
// current step's target unmounts mid-tour, nothing ever calls stop() and the
// user is stuck behind the backdrop. This controller therefore (a) never
// calls start() without registered steps, (b) always stops + clears state on
// error, and (c) runs a watchdog that tears down a stuck overlay.
//
// Only a genuine finish/skip by the user (the copilot `stop` event while the
// tour was showing) or a start() exception marks the tour completed. Timeouts,
// leaving the home screen and watchdog teardowns abort WITHOUT completing, so
// nothing re-triggers it this session; it retries only when onboarding
// triggers it again (walkthroughCompleted is in-memory).
import { useCallback, useEffect, useRef } from 'react';
import { useCopilot } from 'react-native-copilot';
import { useWalkthrough } from '../contexts/WalkthroughContext';

export const START_DELAY_MS = 500;
export const STEP_REGISTRATION_TIMEOUT_MS = 8000;
export const WATCHDOG_INTERVAL_MS = 1000;
export const WATCHDOG_STRIKES = 3;

// True when the step the overlay is pointing at no longer has a mounted target.
export const isStepTargetGone = (step) =>
  !step || !step.wrapperRef || step.wrapperRef.current == null;

// Resolve the current step against the LIVE registered steps (by name) when the
// copilot context exposes them, so a step that re-registered with a fresh ref is
// not judged by the stale object copilot kept in `currentStep`. A step missing
// from a live map is gone. Falls back to `currentStep` when no map is exposed.
export const resolveLiveStep = (currentStep, steps) => {
  if (!currentStep) return undefined;
  if (!steps || typeof steps !== 'object') return currentStep;
  return steps[currentStep.name];
};

const WalkthroughController = ({
  isReady = true,
}) => {
  const { start, stop, copilotEvents, totalStepsNumber, visible, currentStep, steps } = useCopilot();
  const {
    shouldStartWalkthrough,
    onWalkthroughStart,
    onWalkthroughComplete,
    abortWalkthrough,
  } = useWalkthrough();

  // Latest values for timers/cleanup that must not re-subscribe on every change.
  const latest = useRef({});
  latest.current = {
    start, stop, visible, currentStep, steps, totalStepsNumber,
    onWalkthroughComplete, abortWalkthrough,
  };

  // True while WE are stopping the overlay, so the resulting copilot `stop`
  // event is not mistaken for the user finishing the tour.
  const abortingRef = useRef(false);

  // Always remove the overlay and clear walkthrough-active state.
  // markCompleted=false aborts without recording the tour as done.
  const forceStop = useCallback(async ({ markCompleted = true } = {}) => {
    const {
      stop: stopFn,
      onWalkthroughComplete: complete,
      abortWalkthrough: abort,
    } = latest.current;
    abortingRef.current = !markCompleted;
    try {
      await stopFn();
    } catch (err) {
      console.error('Walkthrough stop failed', err);
    } finally {
      abortingRef.current = false;
    }
    if (markCompleted) complete();
    else abort();
  }, []);

  // Listen for copilot events
  useEffect(() => {
    const handleStop = () => {
      if (abortingRef.current) return;
      console.log('Walkthrough completed');
      onWalkthroughComplete();
    };

    const handleStepChange = (step) => {
      console.log('Walkthrough step:', step?.name);
    };

    copilotEvents.on('stop', handleStop);
    copilotEvents.on('stepChange', handleStepChange);

    return () => {
      copilotEvents.off('stop', handleStop);
      copilotEvents.off('stepChange', handleStepChange);
    };
  }, [copilotEvents, onWalkthroughComplete]);

  const hasSteps = totalStepsNumber > 0;
  const wantsStart = shouldStartWalkthrough && isReady;

  // Start the tour only once steps are registered.
  useEffect(() => {
    if (!(wantsStart && hasSteps)) return undefined;
    // Small delay to ensure UI is rendered
    const timer = setTimeout(async () => {
      console.log('Starting walkthrough tour');
      onWalkthroughStart();
      try {
        await latest.current.start();
      } catch (err) {
        console.error('Walkthrough failed to start', err);
        await forceStop();
      }
    }, START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [wantsStart, hasSteps, onWalkthroughStart, forceStop]);

  // No steps ever registered: give up quietly, but do NOT mark the tour done.
  useEffect(() => {
    if (!(wantsStart && !hasSteps)) return undefined;
    const timer = setTimeout(() => {
      console.warn('Walkthrough skipped: no steps registered');
      latest.current.abortWalkthrough();
    }, STEP_REGISTRATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [wantsStart, hasSteps]);

  // Leaving the home screen mid-tour: the steps' targets are gone.
  useEffect(() => {
    if (visible && !isReady) {
      void forceStop({ markCompleted: false });
    }
  }, [visible, isReady, forceStop]);

  // Watchdog: tear down an overlay that has nothing left to point at.
  useEffect(() => {
    if (!visible) return undefined;
    let strikes = 0;
    const interval = setInterval(() => {
      const { totalStepsNumber: total, currentStep: step, steps: liveSteps } = latest.current;
      const liveStep = resolveLiveStep(step, liveSteps);
      strikes = total === 0 || isStepTargetGone(liveStep) ? strikes + 1 : 0;
      if (strikes >= WATCHDOG_STRIKES) {
        clearInterval(interval);
        console.warn('Walkthrough overlay stuck; removing it');
        void forceStop({ markCompleted: false });
      }
    }, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [visible, forceStop]);

  // Never leave the overlay behind if the controller unmounts.
  useEffect(() => () => {
    if (latest.current.visible) {
      void forceStop({ markCompleted: false });
    }
  }, [forceStop]);

  // This component doesn't render anything
  return null;
};

export default WalkthroughController;
